import React, { useState, useEffect, useMemo } from "react";
import { X, Download, Upload, FileSpreadsheet } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import {
  getCertificateTypes,
  getCertificateTemplates,
} from "../../../../api/elearning/certificates/certificates";
import {
  downloadTeacherCertRequestTemplate,
  createTeacherCertificateRequest,
} from "../../../../api/elearning/certificates/certificateRequests";

function readUserBranchId() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return u.branch?.id || u.branch_id || null;
  } catch {
    return null;
  }
}

const TeacherCertificateRequestModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [types, setTypes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [certType, setCertType] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [file, setFile] = useState(null);
  const [branchId, setBranchId] = useState(null);

  const teacherTypes = useMemo(
    () =>
      (types || []).filter(
        (t) => t.recipient_type === "teacher" || t.recipient_type === "both"
      ),
    [types]
  );

  useEffect(() => {
    if (!isOpen) return;
    const bid = readUserBranchId();
    setBranchId(bid);
    setFile(null);
    setCertType("");
    setTemplateId("");
    setLoading(true);
    Promise.all([getCertificateTypes(), getCertificateTemplates("teacher")])
      .then(([ty, tpl]) => {
        const list = ty.types || [];
        setTypes(list);
        const tt = tpl.templates || [];
        setTemplates(tt);
        const teacherOnly = list.filter(
          (t) => t.recipient_type === "teacher" || t.recipient_type === "both"
        );
        const first =
          teacherOnly[0]?.code ||
          list.find((t) => t.code === "teaching_excellence")?.code ||
          list[0]?.code ||
          "teaching_excellence";
        setCertType(first);
        setTemplateId(tt[0]?.id || "");
      })
      .catch(() => {
        setTypes([]);
        setTemplates([]);
        toast.error("Không tải được loại chứng nhận / biểu mẫu.");
      })
      .finally(() => setLoading(false));
  }, [isOpen]);

  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadTeacherCertRequestTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "mau_yeu_cau_chung_nhan_gv.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Không tải được file mẫu.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!branchId) {
      toast.error("Tài khoản chưa gắn chi nhánh.");
      return;
    }
    if (!file) {
      toast.error("Chọn file Excel danh sách giáo viên.");
      return;
    }
    if (!certType) {
      toast.error("Chọn loại chứng nhận.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("cert_type", certType);
      if (templateId) fd.append("template_id", templateId);
      await createTeacherCertificateRequest(fd);
      toast.success("Đã gửi yêu cầu GCN giáo viên.");
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Không thể gửi yêu cầu.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white w-full max-w-lg rounded-[2rem] shadow-xl overflow-hidden flex flex-col max-h-[92vh] relative z-[110]"
        >
          <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
            <div>
              <h2 className="text-xl font-black text-gray-800 uppercase tracking-tight">
                Yêu cầu <span className="text-red-700">GCN giáo viên</span>
              </h2>
              <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mt-1">
                Upload Excel → duyệt chuyên môn → duyệt lãnh đạo
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full text-gray-400 transition-colors"
            >
              <X size={22} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
            {!branchId && (
              <p className="text-sm text-amber-700 font-bold bg-amber-50 border border-amber-100 rounded-xl p-3">
                Tài khoản chưa gắn chi nhánh. Không thể gửi yêu cầu.
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleDownloadTemplate}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-bold text-gray-700 hover:bg-gray-50"
              >
                <Download size={16} /> Tải mẫu Excel
              </button>
            </div>

            <p className="text-xs text-gray-500 leading-relaxed">
              Cột <strong>ma_nv</strong> (mã nhân viên trùng hệ thống) hoặc <strong>teacher_id</strong> (UUID).
              Cột <strong>xep_loai</strong> (tuỳ chọn, mặc định Giỏi). Chỉ nhân viên thuộc chi nhánh của bạn.
            </p>

            <div className="space-y-2">
              <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                Loại chứng nhận
              </label>
              <select
                value={certType}
                onChange={(e) => setCertType(e.target.value)}
                disabled={loading}
                className="w-full bg-gray-50 border border-gray-200 py-3 px-4 rounded-2xl text-sm font-bold outline-none focus:border-red-500"
              >
                {(teacherTypes.length
                  ? teacherTypes
                  : [{ code: "teaching_excellence", name: "Giảng dạy xuất sắc (mặc định)" }]
                ).map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                Biểu mẫu (tuỳ chọn)
              </label>
              <select
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                disabled={loading}
                className="w-full bg-gray-50 border border-gray-200 py-3 px-4 rounded-2xl text-sm font-bold outline-none focus:border-red-500"
              >
                <option value="">— Mặc định hệ thống —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                File Excel đã điền
              </label>
              <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 rounded-2xl py-8 cursor-pointer hover:border-red-300 hover:bg-red-50/30 transition-colors">
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
                {file ? (
                  <span className="text-sm font-bold text-red-800 flex items-center gap-2">
                    <FileSpreadsheet size={20} /> {file.name}
                  </span>
                ) : (
                  <span className="text-sm font-bold text-gray-400 flex items-center gap-2">
                    <Upload size={20} /> Chọn file .xlsx / .csv
                  </span>
                )}
              </label>
            </div>

            <div className="pt-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 rounded-xl font-bold text-gray-500"
              >
                Hủy
              </button>
              <button
                type="submit"
                disabled={submitting || loading || !branchId}
                className="px-6 py-2.5 rounded-xl font-black uppercase text-xs tracking-wide bg-red-700 text-white disabled:opacity-50"
              >
                {submitting ? "Đang gửi…" : "Gửi yêu cầu"}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TeacherCertificateRequestModal;
